import React, { useEffect } from 'react';
import { t } from 'i18next';
import { toast } from 'react-toastify';

import useE164 from '../../utils/E164';
import Button from './Button';
import MuiFooter from './muiControls/muiFooter';
import RenderIf from '../renderIf/RenderIf';
import { FormContext } from '../Context';

function Form(props) {
    const { toE164 } = useE164();
    //Set true by validated elements in order to continue form validation
    const [validated, setValidated] = React.useState(false);
    //Set true by the form to trigger element validation
    const [validate, setValidate] = React.useState(props?.validate ? props.validate : false);

    const findValidatedInputs = React.useCallback(
        (children = props.children) => {
            return React.Children.map(children, (x) => {
                if (x && x.props?.children && x?.type?.displayName !== 'LabeledSelect') {
                    //If the React Element has children and is not a LabeledSelect
                    return findValidatedInputs(x.props.children);
                } else if (x?.type?.displayName === 'LabeledSelect' && x?.props?.formKey) {
                    //If the React Element is a form-bound LabeledSelect
                    return x;
                } else {
                    if (x?.props?.formKey) {
                        //If the React Element is form-bound
                        return x;
                    }
                }
            });
        },
        [props.children]
    );

    //for updating formState on button press
    const formReferences = React.useMemo(() => {
        if (props?.refs) return props.refs;
        else return findValidatedInputs(props.children);
    }, [findValidatedInputs, props]);

    //Form State
    const handleSave = () => {
        let newFormState = {};
        formReferences.forEach(
            (ref) => (newFormState[ref.dataset.formKey] = ref?.dataset?.type === 'phone' ? toE164(ref.value) : ref.value)
        );
        if (typeof props?.onUserInteract?.handleSave === 'function') props.onUserInteract.handleSave(newFormState);
        setValidate(true);
        if (validated) setValidated(false);
    };

    //Form Validation
    useEffect(() => {
        const validSave = props.onValidSave;
        const invalidSave = props.onInvalidSave;
        if (validated) {
            let allValid = formReferences.every((ele) => {
                return ele?.attributes['aria-invalid'] === undefined || ele?.attributes['aria-invalid']?.nodeValue === 'false';
            });
            if (allValid) {
                if (typeof validSave === 'function') validSave();
            } else {
                if (typeof invalidSave === 'function') invalidSave();
                toast.error(t('General.ValidationError'));
            }
            setValidate(false);
            setValidated(false);
        }
    }, [validated, formReferences, props.onValidSave, props.onInvalidSave]);

    return (
        <div className={(props.className ? props.className + ' ' : '') + 'form-component'}>
            <FormContext.Provider value={{ triggerValidation: validate, validationFinished: setValidated }}>
                {props.children}
                <MuiFooter
                    rightContent={[
                        props?.otherButtons,
                        <RenderIf
                            isTrue={props.primaryButton}
                            key="save-button-renderIf"
                        >
                            <Button
                                key="save-button"
                                className="save-button"
                                onClick={handleSave}
                                interactiveText={props?.primaryButtonInteractiveText || 'Submitting'}
                            >
                                {props?.primaryButtonText || 'Submit'}
                            </Button>
                        </RenderIf>,
                    ]}
                    paperSx={{
                        padding: '1rem',
                    }}
                />
            </FormContext.Provider>
        </div>
    );
}

export default Form;
